<template>
    <div class="invoice" id="printMe">
  
      <!-- Loading -->
      <div v-if="loading" class="invoice-loading">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
      </div>
  
      <template v-else>
  
        <!-- Header -->
        <div class="invoice-header">
          <div class="invoice-title-block">
            <h1 class="invoice-title">Purchase Invoice</h1>
            <span class="invoice-batch">{{ purchase?.batch_code }}</span>
          </div>
          <div v-if="company?.logo" class="invoice-logo">
            <img :src="host + company.logo" alt="Company Logo" />
          </div>
        </div>
  
        <div class="invoice-divider"></div>
  
        <!-- Parties -->
        <div class="invoice-parties">
          <!-- From (Company) -->
          <div class="party-block">
            <p class="party-label">From</p>
            <p class="party-name">{{ company?.name }}</p>
            <p class="party-detail" v-if="phone_number?.address">{{ phone_number.address }}</p>
            <p class="party-detail" v-if="phone_number?.phone_number">{{ phone_number.phone_number }}</p>
          </div>
  
          <!-- Supplier -->
          <div class="party-block party-block--right">
            <p class="party-label">Supplier</p>
            <p class="party-name">{{ purchase?.supplier?.supplier_name }}</p>
            <p class="party-detail" v-if="purchase?.supplier?.address">{{ purchase.supplier.address }}</p>
            <p class="party-detail" v-if="purchase?.supplier?.email">{{ purchase.supplier.email }}</p>
          </div>
        </div>
  
        <div class="invoice-divider invoice-divider--dashed"></div>
  
        <!-- Sale Meta -->
        <div class="sale-meta-grid">
          <div class="sale-meta-item">
            <span class="sale-meta-label">Purchase Date</span>
            <span class="sale-meta-value">{{ dateTime(purchase?.date) }}</span>
          </div>
          <div class="sale-meta-item">
            <span class="sale-meta-label">Purchase No.</span>
            <span class="sale-meta-value mono">{{ purchase?.batch_code }}</span>
          </div>
          <div class="sale-meta-item">
            <span class="sale-meta-label">Store</span>
            <span class="sale-meta-value">{{ purchase?.store?.store_name }}</span>
          </div>
          <div class="sale-meta-item">
            <span class="sale-meta-label">Purchased By</span>
            <span class="sale-meta-value">{{ purchase?.user?.fullname }}</span>
          </div>
        </div>
  
        <div class="invoice-divider invoice-divider--dashed"></div>
  
        <!-- Items Table -->
        <div class="table-scroll">
          <table class="invoice-table">
            <thead>
              <tr>
                <th style="width:30%">Product</th>
                <th>Variant</th>
                <th class="text-right">Qty</th>
                <th class="text-right">Cost Price ({{ company?.currency?.symbol }})</th>
                <th class="text-right">Amount ({{ company?.currency?.symbol }})</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in purchase_array" :key="p.id">
                <td class="fw-bold">{{ p?.product_name }}</td>
                <td class="text-muted">{{ p?.variant_name || '—' }}</td>
                <td class="text-right">{{ p?.quantity }}</td>
                <td class="text-right text-muted">{{ $filters.formatMoney(p?.cost_price) }}</td>
                <td class="text-right fw-bold">{{ $filters.formatMoney(p?.total) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
  
        <!-- Total -->
        <div class="totals-block">
          <div class="totals-divider"></div>
          <div class="totals-row totals-row--grand">
            <span class="totals-label">Total</span>
            <span class="totals-value totals-value--grand">
              {{ $filters.formatMoney(total, company?.currency?.symbol) }}
            </span>
          </div>
        </div>
  
        <div class="invoice-divider"></div>
  
        <!-- Payment Information -->
        <div class="payment-block">
          <p class="payment-title">Payment Information</p>
          <div class="payment-grid">
  
            <div class="payment-row">
              <span class="payment-label">Payment Type</span>
              <span class="payment-badge" :class="paymentClass">{{ paymentType }}</span>
            </div>
  
            <div class="payment-row">
              <span class="payment-label">Paid Amount</span>
              <span class="payment-value">
                {{ $filters.formatMoney(
                  purchase?.paid_amount ?? creditor_credits?.amount_paid ?? 0,
                  company?.currency?.symbol
                ) }}
              </span>
            </div>
  
            <!-- Credit / Part: Balance -->
            <div class="payment-row"
              v-if="purchase?.payment_type === 'credit' || purchase?.payment_type === 'part'">
              <span class="payment-label">Balance</span>
              <span class="payment-value"
                :class="parseFloat(creditor_credits?.total_balance ?? purchase?.balance ?? 0) > 0 ? 'text-neg' : ''">
                {{ $filters.formatMoney(
                  creditor_credits?.total_balance ?? purchase?.balance ?? 0,
                  company?.currency?.symbol
                ) }}
              </span>
            </div>
  
            <!-- Split: Cash + Card -->
            <template v-if="purchase?.payment_type === 'split'">
              <div class="payment-row">
                <span class="payment-label">Cash</span>
                <span class="payment-value">{{ $filters.formatMoney(purchase?.cash, company?.currency?.symbol) }}</span>
              </div>
              <div class="payment-row">
                <span class="payment-label">Card</span>
                <span class="payment-value">{{ $filters.formatMoney(purchase?.card, company?.currency?.symbol) }}</span>
              </div>
            </template>
  
          </div>
        </div>
  
      </template>
    </div>
  </template>
  
  <script>
  import { useToast } from 'vue-toastification';
  import { reusableMixin } from '../../../../lib/reusableMixin';
  import { useStore } from 'vuex';
  import host from '../../../../main';
  
  export default {
    props: {
      creditor_credits: { type: Object,  default: () => ({}) },
      purchase_items:   { type: Array,   default: () => [] },
      purchase_array:   { type: Array,   default: () => [] },
      purchase:         { type: Object,  default: () => ({}) },
      loading:          { type: Boolean, default: false },
    },
    mixins: [reusableMixin],
  
    data() {
      return {
        toast: useToast(),
        store: useStore(),
      };
    },
  
    computed: {
      host()        { return host.hostName; },
      loggedInUser(){ return this.store.state.getLoggedInUser; },
  
      total() {
        return (this.purchase_array ?? []).reduce((a, b) => a + parseFloat(b.total ?? 0), 0);
      },
  
      paymentType() {
        const type    = this.purchase?.payment_type;
        const balance = parseFloat(this.creditor_credits?.total_balance ?? this.purchase?.balance ?? 0);
        if ((type === 'part' || type === 'credit') && balance === 0) return 'Paid';
        const map = { cash:'Cash', card:'Card', transfer:'Transfer', part:'Part Payment', split:'Split Payment', credit:'Credit' };
        return map[type] ?? (type ? type.charAt(0).toUpperCase() + type.slice(1) : '—');
      },
  
      paymentClass() {
        const label = this.paymentType;
        if (label === 'Paid')         return 'payment--paid';
        const map = { Cash:'payment--cash', Card:'payment--card', Transfer:'payment--transfer', 'Part Payment':'payment--part', 'Split Payment':'payment--split', Credit:'payment--credit' };
        return map[label] ?? '';
      },
    },
  
    created() { this.getCompany(); },
  };
  </script>
  
  <style scoped>
  /* ── Invoice Shell ── */
  .invoice         { background:#fff; padding:40px 48px; max-width:860px; margin:0 auto; font-size:13.5px; color:#1a2b45; }
  .invoice-loading { display:flex; align-items:center; justify-content:center; padding:80px 0; }
  
  /* ── Header ── */
  .invoice-header      { display:flex; align-items:flex-start; justify-content:space-between; gap:20px; margin-bottom:24px; }
  .invoice-title-block { display:flex; flex-direction:column; gap:8px; }
  .invoice-title       { font-size:36px; font-weight:800; color:#1a2b45; margin:0; line-height:1; }
  .invoice-batch       { font-family:monospace; font-size:14px; font-weight:600; color:#004AAD; background:#edf4ff; border:1px solid #c5d6f5; border-radius:20px; padding:3px 14px; display:inline-block; }
  .invoice-logo        { flex-shrink:0; }
  .invoice-logo img    { max-width:100px; max-height:56px; object-fit:contain; }
  
  /* ── Dividers ── */
  .invoice-divider         { height:1px; background:#e4eaf2; margin:20px 0; }
  .invoice-divider--dashed { border:none; border-top:2px dashed #e4eaf2; background:transparent; }
  
  /* ── Parties ── */
  .invoice-parties     { display:flex; justify-content:space-between; gap:20px; flex-wrap:wrap; }
  .party-block         { display:flex; flex-direction:column; gap:3px; }
  .party-block--right  { text-align:right; }
  .party-label         { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; margin:0 0 4px; }
  .party-name          { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
  .party-detail        { font-size:13px; color:#4a5e78; margin:0; }
  
  /* ── Meta Grid ── */
  .sale-meta-grid  { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; }
  .sale-meta-item  { display:flex; flex-direction:column; gap:4px; }
  .sale-meta-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
  .sale-meta-value { font-size:13.5px; font-weight:600; color:#1a2b45; }
  .mono            { font-family:monospace; }
  
  /* ── Items Table ── */
  .table-scroll      { overflow-x:auto; }
  .invoice-table     { width:100%; border-collapse:collapse; }
  .invoice-table thead tr { border-bottom:2px solid #e4eaf2; background:#f8fafc; }
  .invoice-table thead th { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:10px 12px; text-align:left; white-space:nowrap; }
  .invoice-table tbody tr { border-bottom:1px solid #f0f4f8; }
  .invoice-table tbody tr:last-child { border-bottom:none; }
  .invoice-table tbody td { padding:10px 12px; vertical-align:middle; color:#1a2b45; }
  .text-right  { text-align:right; }
  .text-muted  { color:#4a5e78; }
  .fw-bold     { font-weight:700; }
  
  /* ── Totals ── */
  .totals-block   { display:flex; flex-direction:column; align-items:flex-end; }
  .totals-divider { width:280px; height:1px; background:#e4eaf2; margin:6px 0; }
  .totals-row     { display:flex; justify-content:space-between; width:280px; padding:6px 0; }
  .totals-label   { font-size:13px; font-weight:600; color:#7a90b0; text-transform:uppercase; }
  .totals-row--grand .totals-label { color:#1a2b45; font-size:14px; }
  .totals-value--grand { font-size:20px; font-weight:800; color:#004AAD; }
  
  /* ── Payment ── */
  .payment-block { padding:0; }
  .payment-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 14px; }
  .payment-grid  { display:flex; flex-direction:column; gap:8px; max-width:360px; }
  .payment-row   { display:flex; justify-content:space-between; align-items:center; }
  .payment-label { font-size:13px; color:#7a90b0; }
  .payment-value { font-size:13.5px; font-weight:600; color:#1a2b45; }
  .text-neg      { color:#e03535; }
  
  /* ── Payment Badges ── */
  .payment-badge      { display:inline-block; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; }
  .payment--cash      { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  .payment--paid      { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  .payment--card      { background:#f5f3ff; color:#7c3aed; border-color:#c4b5fd; }
  .payment--transfer  { background:#edf4ff; color:#004AAD; border-color:#c5d6f5; }
  .payment--part,
  .payment--split     { background:#fff7ed; color:#e08c00; border-color:#fed7aa; }
  .payment--credit    { background:#fff0f0; color:#e03535; border-color:#fac5c5; }
  
  /* ── Print ── */
  @media print {
    .invoice { padding:20px; max-width:100%; }
  }
  
  /* ── Responsive ── */
  @media(max-width:768px) {
    .invoice         { padding:24px 20px; }
    .invoice-header  { flex-direction:column; }
    .sale-meta-grid  { grid-template-columns:repeat(2,1fr); }
    .invoice-parties { flex-direction:column; }
    .party-block--right { text-align:left; }
    .totals-row,.totals-divider { width:100%; }
  }
  @media(max-width:480px) { .sale-meta-grid { grid-template-columns:1fr; } }
  </style>